export interface ArticleHub {
  slug: string;
  category: "device" | "persona" | "alternative";
  badge: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  keyPoints: { title: string; desc: string }[];
  comparisonTable?: { label: string; traditional: string; limeshare: string }[];
  steps: { name: string; text: string }[];
  faq: { q: string; a: string }[];
}

export const ARTICLE_HUBS: ArticleHub[] = [
  // Cross-device transfers
  {
    slug: "from-iphone-to-pc",
    category: "device",
    badge: "Cross-Device Transfer",
    title: "How to Send Large Files from iPhone to Windows PC (No Cables, 2026)",
    description: "Transfer large 4K videos and photos from iPhone to Windows PC without iTunes, cables or iCloud. Direct browser-to-browser P2P with LimeDrop.",
    h1: "How to Send Large Files from iPhone to Windows PC",
    lead: "Apple's ecosystem makes AirDrop seamless between iPhones and Macs, but transferring large 4K videos or photo batches to a Windows PC often involves slow iTunes syncing or iCloud web limits. LimeShare lets you transfer files directly from iOS to Windows in your browser.",
    keyPoints: [
      { title: "No iTunes or software to install", desc: "Open Safari on iPhone and Chrome/Edge on Windows — that is all." },
      { title: "Direct P2P over Wi-Fi (LimeDrop)", desc: "Transfers stream directly between devices over local network at 50–100+ MB/s." },
      { title: "Full 4K ProRes quality preserved", desc: "No downscaling, no metadata stripping, 100% bit-for-bit identical." },
    ],
    comparisonTable: [
      { label: "Software required", traditional: "iTunes / iCloud for Windows", limeshare: "None (any browser)" },
      { label: "Cable needed", traditional: "Lightning / USB-C to PC", limeshare: "Wireless (Wi-Fi or Cloud)" },
      { label: "AirDrop to PC", traditional: "Unsupported by Apple", limeshare: "LimeDrop (WebRTC P2P)" },
      { label: "Cloud storage required", traditional: "Consumes iCloud storage", limeshare: "0 cloud storage (LimeDrop) or auto-deleted edge storage" },
    ],
    steps: [
      { name: "Open LimeDrop on iPhone", text: "Open limeshare.org/drop in Safari on your iPhone." },
      { name: "Choose files", text: "Tap to select 4K videos, photo sets, or documents from your Photos or Files app." },
      { name: "Scan QR code on PC", text: "Point your iPhone camera or open the link on your PC to connect directly and stream the file." },
    ],
    faq: [
      {
        q: "Can I AirDrop from iPhone to a Windows PC?",
        a: "Apple AirDrop is strictly restricted to iOS and macOS devices. LimeDrop provides the same instantaneous peer-to-peer Wi-Fi transfer experience across any operating system.",
      },
      {
        q: "What is the maximum file size I can send from iPhone?",
        a: "LimeDrop transfers over direct Wi-Fi have no file size limit. Cloud transfers support up to 3 GB per file for free.",
      },
    ],
  },
  {
    slug: "from-android-to-mac",
    category: "device",
    badge: "Cross-Device Transfer",
    title: "Transfer Large Files from Android to Mac Without Cables (Fast)",
    description: "How to transfer large video and photo files from Android to Mac without Android File Transfer crashes or USB cables. Browser WebRTC P2P.",
    h1: "Transfer Large Files from Android to Mac Wirelessly",
    lead: "Connecting Android to Mac over USB has historically relied on the notoriously buggy 'Android File Transfer' utility. LimeShare eliminates cables, granting Android and Mac users instant high-speed browser-to-browser transfers.",
    keyPoints: [
      { title: "Bypasses Android File Transfer", desc: "No USB cable disconnects, MTP errors, or third-party drivers." },
      { title: "Works across any Wi-Fi or cellular network", desc: "Direct WebRTC DataChannel connection with automatic zero-knowledge cloud fallback." },
      { title: "Zero compression", desc: "Android 8K video, RAW DNGs, and ZIP archives are delivered without re-encoding." },
    ],
    steps: [
      { name: "Select files on Android", text: "Open Chrome on Android and visit limeshare.org or limeshare.org/drop." },
      { name: "Generate transfer link", text: "Tap Encrypt & get link or open LimeDrop P2P mode." },
      { name: "Open on Mac", text: "Open the link in Safari or Chrome on your Mac — the file streams straight to your Downloads folder." },
    ],
    faq: [
      {
        q: "Why does Android File Transfer keep failing on Mac?",
        a: "macOS lacks native MTP filesystem drivers. Android File Transfer crashes frequently on large files. LimeShare bypasses MTP entirely through browser WebRTC.",
      },
    ],
  },
  {
    slug: "from-phone-to-laptop",
    category: "device",
    badge: "Wireless Transfer",
    title: "Send Large Files from Phone to Laptop Wirelessly (No Cloud, 2026)",
    description: "The fastest way to move multi-gigabyte files from your phone to any laptop without cables, apps, or cloud storage. LimeDrop P2P transfer.",
    h1: "Send Large Files from Phone to Laptop Wirelessly",
    lead: "Whether you run iOS, Android, Windows, Mac, or Linux, sending a 2 GB file between phone and laptop shouldn't require email bounces or cloud sync delays. LimeDrop bridges devices over local Wi-Fi with zero setup.",
    keyPoints: [
      { title: "Cross-platform compatibility", desc: "Works across iOS, Android, Windows, macOS, Linux, and Chromebooks." },
      { title: "Uncapped local gigabit speed", desc: "Files stream directly through your router's Wi-Fi network without leaving your building." },
      { title: "Zero data leakage", desc: "Traffic is encrypted peer-to-peer and never touches cloud disks." },
    ],
    steps: [
      { name: "Open LimeDrop on phone", text: "Navigate to limeshare.org/drop." },
      { name: "Pick your files", text: "Select your videos, photos, or archives." },
      { name: "Scan QR code on laptop", text: "Scan the QR code with your phone or open the link on your laptop to download." },
    ],
    faq: [
      {
        q: "Does phone-to-laptop transfer use cellular data?",
        a: "When both devices are on the same Wi-Fi network, LimeDrop routes data locally over your router, consuming zero mobile carrier data.",
      },
    ],
  },

  // Professional / B2B Personas
  {
    slug: "for-lawyers",
    category: "persona",
    badge: "Legal & Compliance",
    title: "Confidential File Sharing for Law Firms (Zero-Knowledge, NDA-Safe)",
    description: "Secure, zero-knowledge file transfer for attorneys, paralegals, and law firms. Client-side AES-256 encryption, certified destruction receipts, no logs.",
    h1: "Confidential Zero-Knowledge File Transfer for Legal Practices",
    lead: "Legal privilege, attorney-client confidentiality, and strict NDAs require that sensitive evidentiary documents, depositions, and litigation discoveries never exist in plaintext on third-party cloud servers. LimeShare's zero-knowledge pipeline guarantees that only sender and recipient hold decryption keys.",
    keyPoints: [
      { title: "Attorney-client privilege preserved", desc: "Our servers store only encrypted binary noise. Subpoenas to hosting providers yield zero plaintext." },
      { title: "Certified Destruction receipts", desc: "Cryptographically signed Ed25519 deletion certificates prove exactly when confidential files were permanently destroyed." },
      { title: "Argon2id password defense", desc: "Password-wrap links so forwarded emails cannot be decrypted without the secondary secret." },
    ],
    comparisonTable: [
      { label: "Plaintext access by provider", traditional: "Dropbox/Box can access unencrypted files", limeshare: "Cryptographically impossible (Zero-Knowledge)" },
      { label: "File indexing / OCR scanning", traditional: "Scanned for search and AI training", limeshare: "Zero indexing or scanning" },
      { label: "Deletion audit trail", traditional: "Unverifiable server logs", limeshare: "Cryptographic Certificate of Destruction" },
    ],
    steps: [
      { name: "Drop legal discovery files", text: "Select up to 3 GB of PDF evidence, briefs, or audio depositions." },
      { name: "Set password & 24h retention", text: "Add a strong Argon2id password and check 'Burn after first download'." },
      { name: "Send link securely", text: "Deliver the link via email and the password via Signal, SMS, or telephone." },
    ],
    faq: [
      {
        q: "Is LimeShare compliant with ABA Model Rules and GDPR confidentiality?",
        a: "Yes. By executing all encryption client-side and never storing keys or raw IPs, LimeShare minimizes data processing risks and ensures confidential data cannot be breached from server infrastructure.",
      },
    ],
  },
  {
    slug: "for-photographers",
    category: "persona",
    badge: "Creative Professionals",
    title: "Client Gallery & RAW File Delivery for Photographers (Full-Res)",
    description: "Deliver pristine RAW shoots and full-resolution print galleries to clients. Zero compression, browser encrypted, ad-free downloads. Up to 3 GB free.",
    h1: "Full-Resolution Client File Delivery for Photographers",
    lead: "Photographers spend thousands on medium-format cameras and prime lenses only to have cloud gallery providers compress high-ISO noise or strip color grading. LimeShare delivers bit-for-bit pristine files with clean, distraction-free download pages.",
    keyPoints: [
      { title: "100% EXIF and color profile fidelity", desc: "Adobe RGB, ProPhoto RGB, and sRGB color profiles remain untouched." },
      { title: "Clean, ad-free client experience", desc: "No distracting third-party banner ads, upsells, or mandatory logins for your clients." },
      { title: "Download all as ZIP in 1 click", desc: "Clients can download individual selects or grab the entire shoot in one streaming ZIP archive." },
    ],
    steps: [
      { name: "Select shoot files", text: "Upload your select RAW files or finished high-res TIFFs and JPEGs." },
      { name: "Get sealed link", text: "Copy the zero-knowledge link or send via WhatsApp/email." },
      { name: "Client downloads or previews", text: "Client views high-res previews in browser or downloads the complete ZIP." },
    ],
    faq: [
      {
        q: "Can clients preview photos before downloading?",
        a: "Yes. LimeShare includes an in-browser zero-knowledge photo previewer that displays high-res images directly in RAM.",
      },
    ],
  },
  {
    slug: "for-videographers",
    category: "persona",
    badge: "Film & Video Editors",
    title: "4K & ProRes Footage Transfer for Video Editors & Filmmakers",
    description: "Fast, uncompressed file delivery for video editors, colorists, and filmmakers. ProRes, 10-bit HDR, multi-track audio. Up to 3 GB free with parallel encrypted streaming.",
    h1: "Fast ProRes & 4K Footage Delivery for Video Editors",
    lead: "Editing workflows require uncompressed master clips, XML timelines, and multi-track audio stems. Uploading gigabytes through slow consumer clouds wastes hours. LimeShare leverages multi-chunk parallel streaming straight to high-speed encrypted edge storage for gigabit transfer speeds.",
    keyPoints: [
      { title: "Native ProRes & 10-bit Rec.2020", desc: "Bit-perfect preservation of color grading, LOG profiles, and timecode tracks." },
      { title: "Parallel multi-part upload engine", desc: "Saturates your broadband connection using concurrent 8–64 MB chunks." },
      { title: "Automatic upload resume", desc: "Network drop during a 3 GB upload? LimeShare resumes from the exact missing byte." },
    ],
    steps: [
      { name: "Export master cut or stems", text: "Export your master ProRes or H.265 timeline." },
      { name: "Drop into LimeShare", text: "Uploads stream in parallel chunks with live MB/s and ETA indicators." },
      { name: "Share link with colorist", text: "Recipient streams or downloads the exact file directly to their RAID drive." },
    ],
    faq: [
      {
        q: "What video formats can editors transfer?",
        a: "Any format: Apple ProRes 422/HQ, Avid DNxHD, H.264, H.265, Blackmagic BRAW, RED R3D, and QuickTime MOV.",
      },
    ],
  },
  {
    slug: "for-accountants",
    category: "persona",
    badge: "Financial & Tax",
    title: "Secure Tax, Audit & Financial Document Transfer for Accountants",
    description: "Transfer tax returns, audit workpapers, and payroll records securely. Zero-knowledge browser encryption, self-destructing links, no data leaks.",
    h1: "Secure Tax & Financial Document Transfer for Accounting Firms",
    lead: "Tax forms (W-2, 1040, corporate returns) contain sensitive Social Security numbers, bank routing information, and financial secrets. Sending tax returns as standard email attachments is a major compliance risk. LimeShare ensures financial documents remain sealed end-to-end.",
    keyPoints: [
      { title: "Protects against tax identity theft", desc: "Client SSNs and payroll spreadsheets cannot be intercepted from email servers." },
      { title: "Zero client registration friction", desc: "Elderly or non-technical clients can download or upload tax files without remembering portal passwords." },
      { title: "Burn after first download", desc: "Links disappear immediately once the client pulls their completed tax return." },
    ],
    steps: [
      { name: "Encrypt tax package", text: "Drop your PDF tax returns or Excel audit schedules." },
      { name: "Add password & burn-on-read", text: "Require an Argon2id password and enable burn on read." },
      { name: "Deliver link to client", text: "Client unlocks the files securely in their browser." },
    ],
    faq: [
      {
        q: "Is LimeShare safer than emailing a password-protected PDF?",
        a: "Yes. Standard PDF password encryption is notoriously weak and can often be cracked with GPU tools in minutes. LimeShare utilizes military-grade AES-256-GCM combined with Argon2id key derivation.",
      },
    ],
  },

  // Competitor Alternatives
  {
    slug: "swisstransfer-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "SwissTransfer Alternative with Client-Side Zero-Knowledge Encryption",
    description: "Looking for a SwissTransfer alternative with real end-to-end encryption? Compare SwissTransfer vs LimeShare: privacy, speed, and security.",
    h1: "SwissTransfer Alternative: Why Client-Side Encryption Matters",
    lead: "SwissTransfer (by Infomaniak) is widely respected for generous 50 GB free limits and hosting in Switzerland. However, SwissTransfer is NOT end-to-end encrypted — their servers hold your unencrypted files. LimeShare encrypts everything inside your browser before upload.",
    keyPoints: [
      { title: "Zero-Knowledge vs Server-Side Trust", desc: "SwissTransfer asks you to trust Swiss data centers; LimeShare requires no trust because we never hold the key." },
      { title: "Encrypted File Names & Manifest", desc: "Even metadata and file names are cryptographically hidden in LimeShare." },
      { title: "Instant Upload Resume", desc: "LimeShare saves progress in IndexedDB so large uploads can resume after drops." },
    ],
    comparisonTable: [
      { label: "Encryption type", traditional: "Server-side at rest (Infomaniak holds keys)", limeshare: "Client-side AES-256-GCM (Zero-Knowledge)" },
      { label: "File names visible to host", traditional: "Yes", limeshare: "No (encrypted manifest)" },
      { label: "Upload resume support", traditional: "Limited", limeshare: "Full IndexedDB frame resume" },
      { label: "Direct P2P option", traditional: "No", limeshare: "Yes (LimeDrop WebRTC)" },
    ],
    steps: [
      { name: "Drop files on LimeShare", text: "Add your files (up to 3 GB free)." },
      { name: "Browser encrypts", text: "AES-256-GCM container generated locally in RAM." },
      { name: "Share sealed link", text: "Recipient decrypts directly in their browser." },
    ],
    faq: [
      {
        q: "Is SwissTransfer end-to-end encrypted?",
        a: "No. SwissTransfer encrypts files at rest on Infomaniak's servers, meaning Infomaniak has the cryptographic capability to decrypt and view the files. LimeShare is 100% zero-knowledge end-to-end encrypted.",
      },
    ],
  },
  {
    slug: "firefox-send-alternative",
    category: "alternative",
    badge: "Revived Protocol",
    title: "Firefox Send Alternative in 2026: Zero-Knowledge & Open Source",
    description: "Missing Firefox Send? LimeShare is the modern, open-source revival of zero-knowledge file sharing with 3GB free limits, resume, and no accounts.",
    h1: "The Modern Firefox Send Alternative for 2026",
    lead: "When Mozilla shut down Firefox Send in 2020, millions of privacy-conscious users lost their favorite client-encrypted file transfer tool. LimeShare was architected on the same core principle: full client-side AES-GCM encryption with keys held strictly in the URL fragment.",
    keyPoints: [
      { title: "The spiritual successor to Firefox Send", desc: "Zero accounts, browser-based cryptography, self-destructing links, and open-source protocol." },
      { title: "Upgraded for 2026 scale", desc: "3 GB per file (vs 1 GB in Firefox Send), parallel multi-part encrypted streams, and instant resume." },
      { title: "Client-side ZIP64 and streaming sinks", desc: "Stream multi-gigabyte downloads directly to disk without memory crashes." },
    ],
    comparisonTable: [
      { label: "Current status", traditional: "Defunct (Discontinued 2020)", limeshare: "Active & High-Speed (2026)" },
      { label: "Free size limit", traditional: "1 GB (1 file)", limeshare: "3 GB per file (up to 3 files)" },
      { label: "Encryption protocol", traditional: "WebCrypto AES-GCM", limeshare: "LSC1 STREAM (AES-256-GCM + HKDF)" },
      { label: "P2P transfer", traditional: "No", limeshare: "Yes (LimeDrop)" },
    ],
    steps: [
      { name: "Open LimeShare", text: "No installation required — works in any modern browser." },
      { name: "Drop your files", text: "Files are encrypted in 4 MB frames before sending." },
      { name: "Share link with hash key", text: "The decryption key stays in the URL fragment (#) and never touches our servers." },
    ],
    faq: [
      {
        q: "Why was Firefox Send shut down?",
        a: "Mozilla discontinued Firefox Send due to abuse by malware actors and organizational refocusing. LimeShare incorporates invisible Turnstile bot protection, daily IP budget ceilings, and strict abuse intake pipelines to ensure sustainable operation.",
      },
    ],
  },
  {
    slug: "for-architects",
    category: "persona",
    badge: "Architecture & Engineering",
    title: "Large CAD, BIM & Blueprint Transfer for Architects & Engineers",
    description: "Send large AutoCAD DWG, Revit BIM models, and IFC project files to contractors and structural engineers. Zero-knowledge encrypted, up to 3 GB free.",
    h1: "Secure Large CAD & BIM File Transfer for Architects",
    lead: "Architecture and engineering practices coordinate gigabytes of multi-discipline Revit central models, structural point clouds, and high-DPI rendering passes. LimeShare enables secure, frictionless file exchange with subcontractors, clients, and municipal reviewers without expensive cloud licenses.",
    keyPoints: [
      { title: "No software licenses required for recipients", desc: "Contractors, steel fabricators, and clients download drawings instantly in their browser." },
      { title: "Protects proprietary building IP", desc: "Commercial structural schematics and MEP engineering models remain encrypted end-to-end." },
      { title: "Parallel multi-part encrypted pipe", desc: "Upload multi-gigabyte models in minutes over high-speed optical connections." },
    ],
    steps: [
      { name: "Archive drawing set", text: "Select your RVT, DWG, or IFC model package." },
      { name: "Generate sealed link", text: "Encrypt client-side and set a 48-hour expiration." },
      { name: "Deliver to contractor", text: "Subcontractors stream the exact CAD archive directly to their desktop." },
    ],
    faq: [
      {
        q: "Can I transfer Revit central models without corruption?",
        a: "Yes. LimeShare's STREAM AEAD container verifies every 4 MB chunk with authentication tags, guaranteeing zero bit corruption.",
      },
    ],
  },
  {
    slug: "for-healthcare",
    category: "persona",
    badge: "Healthcare & Medical",
    title: "Confidential DICOM Imaging & Medical Record Transfer",
    description: "Transfer high-resolution DICOM MRI, CT scans, and patient files securely. Zero-knowledge client-side encryption, auto-purged links, no logs.",
    h1: "Confidential DICOM Medical Scan & Patient Record Transfer",
    lead: "Medical imaging datasets (DICOM series, multi-slice CT, functional MRI) contain thousands of uncompressed slices totaling hundreds of megabytes. Protecting Protected Health Information (PHI) under HIPAA and GDPR Article 9 requires absolute cryptographic confidentiality.",
    keyPoints: [
      { title: "Zero Protected Health Information (PHI) exposure", desc: "Files and patient metadata are encrypted in the browser before touching the network. Cloud storage holds only encrypted noise." },
      { title: "Certified Destruction audit trail", desc: "Download or automatic expiry triggers an Ed25519 cryptographic certificate of permanent erasure." },
      { title: "Burn-on-read for single-consultant handoffs", desc: "Files vanish permanently from cloud disks immediately after the specialist downloads them." },
    ],
    steps: [
      { name: "Export DICOM folder", text: "Export the imaging study as a ZIP or multi-file set." },
      { name: "Add password & burn-on-read", text: "Set a strong Argon2id password and enable 'Burn after first download'." },
      { name: "Send link to specialist", text: "Consultant downloads the study securely without needing an account." },
    ],
    faq: [
      {
        q: "Does LimeShare store patient medical records?",
        a: "Never. The server receives only AES-256 encrypted ciphertext with no access to the decryption key. Files are purged automatically after download or expiry.",
      },
    ],
  },
  {
    slug: "for-game-developers",
    category: "persona",
    badge: "Game Development",
    title: "Fast Game Build & Unreal Engine Asset Delivery for Studios",
    description: "Share large Unreal Engine, Unity builds, and 8K texture packs with playtesters and remote artists. High-speed parallel encrypted streaming up to 3 GB free.",
    h1: "Fast Game Build & Asset Distribution for Game Studios",
    lead: "Game development is notorious for massive files: developmental game builds, uncooked Unreal Engine levels, and multi-gigabyte Substance Painter texture libraries. LimeShare gives independent studios and AAA remote teams an unthrottled high-speed distribution pipeline.",
    keyPoints: [
      { title: "Uncapped gigabit download speeds", desc: "Playtesters download builds at the full capacity of their broadband connection via high-speed edge distribution." },
      { title: "Automatic resume on Wi-Fi drops", desc: "If an artist's upload is interrupted, LimeShare resumes from the exact missing byte." },
      { title: "Zero cloud storage fees", desc: "Share up to 3 GB per batch for free with no egress bandwidth fees." },
    ],
    steps: [
      { name: "Package game build", text: "Compress your standalone build into a ZIP archive." },
      { name: "Drop into LimeShare", text: "Monitor live upload speed in MB/s with ETA." },
      { name: "Share with QA / Playtesters", text: "Send the self-destructing link to your Discord community or testing team." },
    ],
    faq: [
      {
        q: "Can playtesters download builds without registering?",
        a: "Yes. They click the link and the download begins immediately in their browser.",
      },
    ],
  },
  {
    slug: "for-musicians-podcasters",
    category: "persona",
    badge: "Audio & Music",
    title: "Lossless Audio Stems & Master Recording Transfer for Musicians",
    description: "Send 24-bit WAV audio stems, mix sessions, and podcast master tracks to mixing engineers. In-browser lossless audio preview, up to 3 GB free.",
    h1: "Lossless Audio Stems & Mix Session Delivery for Audio Engineers",
    lead: "Studio sessions require uncompressed 24-bit 96kHz multi-track audio stems, Pro Tools sessions, and lossless WAV masters. Standard messaging platforms destroy sound with lossy MP3 compression. LimeShare guarantees bit-perfect audio delivery with an in-browser lossless player.",
    keyPoints: [
      { title: "In-browser lossless audio player", desc: "Collaborators can listen to WAV and FLAC masters directly in their browser before downloading." },
      { title: "100% bit-for-bit studio fidelity", desc: "No resampling, no dynamic range compression, zero dither alteration." },
      { title: "One-click 'Download all (ZIP)'", desc: "Engineers can download the entire stems package in one clean streaming archive." },
    ],
    steps: [
      { name: "Export session stems", text: "Export your 24-bit WAV or FLAC multi-tracks." },
      { name: "Drop into LimeShare", text: "Add an optional note with BPM, key, and mixing instructions." },
      { name: "Deliver to mix engineer", text: "Recipient previews the audio or downloads all stems in one click." },
    ],
    faq: [
      {
        q: "Can the recipient listen to audio before downloading?",
        a: "Yes. LimeShare has an integrated zero-knowledge media preview player for WAV, FLAC, and MP3 files.",
      },
    ],
  },
  {
    slug: "smash-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "FromSmash Alternative with End-to-End Encryption & Fast Speeds",
    description: "Looking for a Smash (FromSmash) alternative? Compare Smash vs LimeShare: unthrottled gigabit speeds, zero-knowledge encryption, and no upload queues.",
    h1: "The Fast, Zero-Knowledge Smash (FromSmash) Alternative",
    lead: "FromSmash gained popularity by offering free file transfers without rigid size limits. However, on free accounts, Smash places files larger than 2 GB into slow processing queues and throttles download speeds. LimeShare offers unthrottled gigabit speeds with client-side AES-GCM encryption.",
    keyPoints: [
      { title: "No download throttling or queues", desc: "Smash deliberately delays and queues large free files; LimeShare transfers at full wire speed." },
      { title: "Client-side encryption vs server trust", desc: "Smash holds keys on its servers; LimeShare is mathematically zero-knowledge." },
      { title: "Direct P2P LimeDrop mode", desc: "Transfer directly device-to-device over local Wi-Fi without uploading to any server." },
    ],
    comparisonTable: [
      { label: "Download speed (free)", traditional: "Throttled / queued above 2 GB", limeshare: "Unthrottled gigabit edge network" },
      { label: "End-to-End Encryption", traditional: "No (server holds keys)", limeshare: "Yes (AES-256-GCM in browser)" },
      { label: "Ad banners on download", traditional: "Custom branding / ads", limeshare: "Zero advertisements" },
    ],
    steps: [
      { name: "Drop files on LimeShare", text: "Add up to 3 files (3 GB each) with no wait queues." },
      { name: "Instant encryption", text: "Encrypted locally in RAM with no server delays." },
      { name: "Fast recipient download", text: "Recipient gets full unthrottled speed immediately." },
    ],
    faq: [
      {
        q: "Why is Smash slow for files over 2 GB?",
        a: "Smash uses a 'priority queue' algorithm on its free tier to incentivize users to upgrade to Smash Pro. LimeShare uses zero-egress encrypted edge infrastructure, enabling free unthrottled transfers.",
      },
    ],
  },
  {
    slug: "send-anywhere-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "Send Anywhere Alternative Without Aggressive Ads or Bloatware",
    description: "Looking for a clean Send Anywhere alternative? Compare Send Anywhere vs LimeShare: zero ads, browser-native WebRTC P2P, and no 6-digit timeout stress.",
    h1: "A Clean, Ad-Free Send Anywhere Alternative",
    lead: "Send Anywhere pioneered 6-digit key transfers, but has become overloaded with intrusive video ads, app install prompts, and tight 10-minute timeout windows. LimeShare delivers the same direct P2P transfer capabilities in a clean, private, ad-free interface.",
    keyPoints: [
      { title: "100% Ad-free experience", desc: "No full-screen pop-ups, video ads, or tracking cookies for you or your recipient." },
      { title: "No software or mobile app required", desc: "Runs directly in mobile and desktop browsers with PWA install support." },
      { title: "Flexible expiration", desc: "Choose between 1 hour, 6 hours, 24 hours, or 48 hours — not just 10 minutes." },
    ],
    comparisonTable: [
      { label: "Advertising", traditional: "Heavy video and banner ads", limeshare: "100% Ad-Free" },
      { label: "Software installation", traditional: "Pushes mobile / desktop apps", limeshare: "Zero installation (Pure Web)" },
      { label: "P2P transfer mode", traditional: "Yes (6-digit key)", limeshare: "Yes (LimeDrop WebRTC & QR)" },
    ],
    steps: [
      { name: "Open LimeDrop", text: "Visit limeshare.org/drop." },
      { name: "Connect with QR code", text: "Scan the QR code on the receiving phone or laptop." },
      { name: "Stream directly", text: "Files transfer over local Wi-Fi with no ads." },
    ],
    faq: [
      {
        q: "Can I transfer between phone and PC like Send Anywhere?",
        a: "Yes. LimeDrop uses WebRTC DataChannels to transfer files directly between any mobile phone and computer on the same Wi-Fi network.",
      },
    ],
  },
  {
    slug: "filemail-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "Filemail Alternative with Zero-Knowledge Privacy & Free Transfers",
    description: "Compare Filemail vs LimeShare: browser end-to-end encryption, no email address harvesting, and free 3GB transfers with zero tracking.",
    h1: "The Private, Zero-Knowledge Filemail Alternative",
    lead: "Filemail is a widely known utility for emailing large files, but requires entering sender and recipient email addresses and stores files unencrypted on its servers. LimeShare operates on strict data minimization: no email required, client-side AES-256 encryption, and self-destructing links.",
    keyPoints: [
      { title: "Zero email harvesting", desc: "No need to enter your email or your recipient's email address to send files." },
      { title: "End-to-end client encryption", desc: "Filemail holds decryption keys; LimeShare mathematically cannot read your files." },
      { title: "Auto-purge protection", desc: "Files delete automatically upon first download or expiration." },
    ],
    comparisonTable: [
      { label: "Email addresses required", traditional: "Mandatory sender and recipient emails", limeshare: "None (anonymous link)" },
      { label: "Client-side encryption", traditional: "Only on expensive Enterprise tiers", limeshare: "Free for all transfers (AES-256-GCM)" },
      { label: "Tracking & analytics", traditional: "Tracks recipient IP and open status", limeshare: "Zero recipient tracking" },
    ],
    steps: [
      { name: "Drop files", text: "Add up to 3 GB without entering email addresses." },
      { name: "Copy link", text: "Share via any messaging app or email client you prefer." },
      { name: "Recipient decrypts", text: "Files stream directly to disk without login." },
    ],
    faq: [
      {
        q: "Does LimeShare require my email address?",
        a: "No. You can send files up to 3 GB completely anonymously with zero sign-up.",
      },
    ],
  },
  {
    slug: "transfernow-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "TransferNow Alternative with Real End-to-End Encryption",
    description: "Looking for a modern TransferNow alternative? Compare TransferNow vs LimeShare: zero ads, client-side encryption, and instant P2P transfers.",
    h1: "A Cleaner, Truly Encrypted TransferNow Alternative",
    lead: "TransferNow offers 5 GB free transfers supported by heavy advertising networks and cookie tracking. LimeShare delivers an ad-free, privacy-first transfer experience powered by client-side WebCrypto and high-speed encrypted edge infrastructure.",
    keyPoints: [
      { title: "No advertising trackers", desc: "TransferNow uses ad networks that track users across the web; LimeShare has zero third-party scripts." },
      { title: "Zero-Knowledge LSC1 STREAM", desc: "Files are encrypted in your browser before upload, not on a third-party server." },
      { title: "Certified Destruction receipts", desc: "Get verifiable proof when confidential files are deleted." },
    ],
    steps: [
      { name: "Upload on LimeShare", text: "Fast parallel multi-part encrypted upload." },
      { name: "Share link", text: "Deliver link with encryption key in the hash fragment." },
      { name: "Clean recipient view", text: "Zero ads, instant download and media preview." },
    ],
    faq: [
      {
        q: "Why is LimeShare better than TransferNow for commercial work?",
        a: "LimeShare provides clean, professional download pages with zero third-party advertising, ensuring your clients see only your files.",
      },
    ],
  },
  {
    slug: "dropbox-transfer-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "Dropbox Transfer Alternative (No Account Needed, Up to 3 GB Free)",
    description: "Compare Dropbox Transfer vs LimeShare: send files up to 3GB without a Dropbox account, subscription, or cloud storage quotas.",
    h1: "Free Dropbox Transfer Alternative Without Account Limits",
    lead: "Dropbox Transfer allows users to send files without granting edit access to shared folders, but requires a Dropbox account and restricts free users to a tiny 100 MB limit. LimeShare provides up to 3 GB free transfers with zero sign-up and end-to-end client encryption.",
    keyPoints: [
      { title: "3 GB free vs 100 MB on Dropbox", desc: "Send 30x larger files on LimeShare's free tier without paying for Dropbox Plus." },
      { title: "Zero account required for anyone", desc: "Neither sender nor recipient needs a Dropbox account or mobile app." },
      { title: "Zero cloud quota consumption", desc: "Transfers do not eat into your personal cloud storage space." },
    ],
    comparisonTable: [
      { label: "Free transfer limit", traditional: "100 MB (Requires Dropbox account)", limeshare: "3 GB (Zero account required)" },
      { label: "End-to-End Encryption", traditional: "No (Dropbox stores plaintext)", limeshare: "Yes (AES-256-GCM in browser)" },
      { label: "Account needed", traditional: "Mandatory sender login", limeshare: "100% Anonymous" },
    ],
    steps: [
      { name: "Drop files", text: "Upload up to 3 GB without logging into Dropbox." },
      { name: "Get sealed link", text: "The encryption key is embedded in the URL fragment." },
      { name: "Recipient downloads", text: "Direct stream to disk with no Dropbox prompts." },
    ],
    faq: [
      {
        q: "Do I need a Dropbox account to use LimeShare?",
        a: "No. LimeShare is 100% independent and requires zero accounts for senders or recipients.",
      },
    ],
  },
  {
    slug: "wormhole-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "Wormhole Alternative: Zero-Knowledge File Transfer with Resume",
    description: "Looking for an alternative to Wormhole.app? Compare Wormhole vs LimeShare: up to 3GB cloud transfers, LimeDrop P2P, and upload resume.",
    h1: "The Resumable, High-Speed Wormhole Alternative",
    lead: "Wormhole.app demonstrated the elegance of zero-knowledge browser file sharing. LimeShare builds on that foundation, adding multi-part parallel encrypted uploads, IndexedDB upload resume, client-side ZIP streaming, and Certified Destruction receipts.",
    keyPoints: [
      { title: "IndexedDB upload resume", desc: "Wormhole uploads must start from scratch if disconnected; LimeShare resumes from the exact missing byte." },
    ],
    steps: [
      { name: "Select files", text: "Choose your files for high-speed parallel upload." },
      { name: "Share link", text: "Encrypted link with key in hash fragment." },
      { name: "Recipient downloads", text: "Stream directly to filesystem or preview in browser." },
    ],
    faq: [
      {
        q: "How does LimeShare compare to Wormhole in security?",
        a: "Both use AES-GCM client-side encryption. LimeShare uses the formal STREAM AEAD construction (LSC1), preventing frame reordering, truncation, and metadata leakage.",
      },
    ],
  },
  {
    slug: "sendgb-alternative",
    category: "alternative",
    badge: "Competitor Comparison",
    title: "SendGB Alternative with True Client-Side Zero-Knowledge Encryption",
    description: "Compare SendGB vs LimeShare: both offer 5GB free transfers without signup, but LimeShare encrypts inside your browser and includes direct WebRTC P2P.",
    h1: "The Zero-Knowledge SendGB Alternative (5 GB Free + Client Encryption)",
    lead: "SendGB is a popular utility for sending 5 GB files without registration. However, SendGB uses server-side at-rest encryption — meaning their infrastructure holds the decryption keys. LimeShare matches the 5 GB / 500-file free limit while encrypting every byte inside your browser before upload.",
    keyPoints: [
      { title: "Client-Side Zero-Knowledge vs Server-Side At-Rest", desc: "SendGB encrypts on the server; LimeShare encrypts in your browser RAM so not even our servers can see file names or contents." },
      { title: "Recipient Timer Rescue", desc: "On LimeShare, either the sender OR the recipient can freeze the expiration timer and keep files online for 1 year." },
      { title: "Built-in LimeDrop P2P & Offline Encryptor", desc: "Transfer unlimited sizes directly over local Wi-Fi without cloud storage, or encrypt files offline." },
    ],
    comparisonTable: [
      { label: "Free Transfer Limit", traditional: "5 GB (500 files)", limeshare: "5 GB (500 files / folders)" },
      { label: "Encryption Architecture", traditional: "Server-side (company holds keys)", limeshare: "Client-side Zero-Knowledge (LSC1)" },
      { label: "Direct Browser P2P Mode", traditional: "No", limeshare: "Yes (LimeDrop WebRTC)" },
      { label: "Certified Destruction Proof", traditional: "No", limeshare: "Yes (Cryptographically signed receipt)" },
      { label: "25-Person Team Plan", traditional: "20 users · €199.90/yr", limeshare: "25 users · $149/yr" },
    ],
    steps: [
      { name: "Drop files or folders", text: "Select up to 5 GB (500 files) for free, or up to 1 TB with an Exceed Pass." },
      { name: "Browser encrypts", text: "AES-256-GCM chunks stream directly to zero-egress storage." },
      { name: "Share sealed link", text: "Recipient decrypts locally or downloads all as a streaming ZIP." },
    ],
    faq: [
      {
        q: "Is SendGB end-to-end encrypted?",
        a: "SendGB uses TLS in transit and AES-256 at rest on cloud servers, meaning the service provider holds the keys. LimeShare generates the key in your browser and keeps it strictly in the URL fragment (#), providing mathematical zero-knowledge.",
      },
    ],
  },
  {
    slug: "send-over-25-mb",
    category: "device",
    badge: "By File Size",
    title: "Send Files Over 25 MB Free (Bypass Gmail & Outlook Limits)",
    description: "Gmail stops at 25 MB and Outlook at 20 MB. Send files over 25 MB up to 5 GB free with a zero-knowledge link — kept online for up to 30 days.",
    h1: "Send Files Over 25 MB Free Without Email Bounces",
    lead: "Gmail's attachment limit is 25 MB (and roughly 18.5 MB in practice due to Base64 encoding). Outlook stops at 20 MB. Instead of compressing your PDF, presentation, or video, send an encrypted link up to 5 GB free.",
    keyPoints: [
      { title: "Kept online for 30 days free", desc: "With Smart Gravity Retention, any transfer under 100 MB stays available for a full 30 days at zero cost." },
      { title: "No Google Drive or OneDrive permission headaches", desc: "Recipients click the link and download immediately without logging into Google or Microsoft." },
      { title: "100% original quality", desc: "Presentations, PDFs, and videos are delivered bit-for-bit identical." },
    ],
    steps: [
      { name: "Drop your file over 25 MB", text: "Add your PDF, video, or ZIP to the upload box." },
      { name: "Copy the link", text: "Your browser encrypts the file and gives you a short link." },
      { name: "Paste into your email", text: "Send the link in Gmail or Outlook — zero attachment bounce." },
    ],
    faq: [
      {
        q: "How long does a 40 MB file stay online?",
        a: "Free transfers under 100 MB stay online for 30 days automatically (or can be extended to 1 year).",
      },
    ],
  },
  {
    slug: "send-over-100-mb",
    category: "device",
    badge: "By File Size",
    title: "Send Files Over 100 MB Free Without Signing Up",
    description: "Send 100 MB to 500 MB presentations, photo albums, and Discord clips for free. No registration, client-side AES-256 encryption.",
    h1: "Send Files Over 100 MB Free Without Registration",
    lead: "Presentations with embedded video, high-DPI PDF catalogs, and photo albums routinely weigh 100 MB to 500 MB — far above email, Discord (10 MB), and Signal (100 MB) limits. Send them in seconds with LimeShare.",
    keyPoints: [
      { title: "Well within the 5 GB free limit", desc: "Send 100 MB, 250 MB, or 500 MB completely free with zero account creation." },
      { title: "7-day automatic retention", desc: "Transfers between 100 MB and 1 GB stay online for 7 full days free." },
      { title: "In-browser media preview", desc: "Recipients can preview videos, audio, and images before saving." },
    ],
    steps: [
      { name: "Add files or folder", text: "Select up to 500 files (up to 5 GB total)." },
      { name: "Encrypt in browser", text: "Streaming AES-256-GCM encryption at wire speed." },
      { name: "Share anywhere", text: "Paste the link into email, Discord, Slack, or WhatsApp." },
    ],
    faq: [
      {
        q: "Do I need an account to send a 250 MB file?",
        a: "No. Everything up to 5 GB is free and requires no signup.",
      },
    ],
  },
  {
    slug: "send-over-2-gb",
    category: "device",
    badge: "By File Size",
    title: "Send Files Over 2 GB Free Without Signing Up (Up to 5 GB)",
    description: "Many free services stop at 2 GB. With LimeShare, send files and folders between 2 GB and 5 GB free without registration or splitting archives.",
    h1: "Send Files Over 2 GB Free Without Signing Up",
    lead: "Many legacy file transfer tools hit a paywall at 2 GB, forcing you to split project folders into parts or register an account. On LimeShare, the free limit is 5 GB — so a 3.2 GB video shoot or project folder transfers in one link.",
    keyPoints: [
      { title: "No 2 GB paywall", desc: "Transfers from 2 GB to 5 GB are 100% free and require no account." },
      { title: "Don't split archives into parts", desc: "Send a single 4.5 GB file or a 500-file folder under one link." },
      { title: "Automatic upload resume", desc: "If your connection drops at 3.1 GB, IndexedDB resume continues from the exact missing byte." },
    ],
    comparisonTable: [
      { label: "Email attachment", traditional: "Fails above 20–25 MB", limeshare: "Paste link in any email" },
      { label: "2 GB free services", traditional: "Paywall or forced signup at 2 GB", limeshare: "Free up to 5 GB (no signup)" },
      { label: "WhatsApp document", traditional: "Stops at 2 GB", limeshare: "Up to 5 GB free (1 TB with Exceed)" },
    ],
    steps: [
      { name: "Add your 2–5 GB file or folder", text: "Drop a single large video or an entire project directory." },
      { name: "Stream upload", text: "Parallel multi-part chunks saturate your broadband connection." },
      { name: "Recipient downloads", text: "Streams straight to disk with zero memory crashes." },
    ],
    faq: [
      {
        q: "Can I send a 3.5 GB file for free?",
        a: "Yes. Any transfer up to 5 GB is completely free without registration.",
      },
    ],
  },
  {
    slug: "send-over-5-gb",
    category: "device",
    badge: "By File Size",
    title: "Send Files Over 5 GB to 50 GB Online Without a Subscription",
    description: "Need to send 10 GB, 20 GB, or 50 GB once? Use LimeShare Exceed ($2.49) or LimeDrop P2P (free) without buying a monthly subscription.",
    h1: "Send Files Over 5 GB (10 GB, 20 GB, 50 GB) Without a Subscription",
    lead: "When a 4K video master, camera card dump, or backup archive exceeds the 5 GB free ceiling, you have two choices on LimeShare: transfer it for free over direct P2P with LimeDrop, or use a one-time $2.49 Exceed Pass without any subscription.",
    keyPoints: [
      { title: "Option 1: Free unlimited size via LimeDrop P2P", desc: "If both devices are online, stream 10 GB or 100 GB directly browser-to-browser for $0." },
      { title: "Option 2: One-time Cloud Exceed Pass ($2.49)", desc: "Send up to 50 GB (or $4.49 for 200 GB) stored in the cloud for 7–14 days with zero signup." },
      { title: "No recurring subscription trap", desc: "Pay only for the single transfer you need." },
    ],
    steps: [
      { name: "Drop your 5–50 GB files", text: "Select your files or folder on the homepage." },
      { name: "Pick Exceed 50 GB ($2.49) or LimeDrop P2P", text: "Unlock instant cloud capacity with crypto or stream peer-to-peer." },
      { name: "Deliver link", text: "Recipient downloads at full gigabit speed." },
    ],
    faq: [
      {
        q: "Do I need a yearly plan to send a 15 GB file once?",
        a: "No. Use a one-time Exceed 50 GB pass ($2.49) or use LimeDrop P2P for free.",
      },
    ],
  },
  {
    slug: "send-1-tb-files",
    category: "device",
    badge: "Massive Transfers",
    title: "Send 500 GB and 1 TB Files Online (No Subscription Needed)",
    description: "Transfer massive 200 GB, 500 GB, and 1 TB production archives, raw cinema footage, and disk backups online with zero-knowledge encryption.",
    h1: "How to Send 500 GB and 1 TB Files Online",
    lead: "Full cinema camera cards (RED R3D, ARRI Alexa), server backups, and dataset archives reach hundreds of gigabytes to 1 TB. LimeShare's 64 MiB multi-part streaming engine and one-time 1 TB Exceed Pass ($8.99) handle massive payloads without browser memory crashes.",
    keyPoints: [
      { title: "O(1) RAM streaming architecture", desc: "Encrypts in 4 MiB frames and uploads in 64 MiB parts — memory stays under 150 MB even on a 500 GB file." },
      { title: "One-time 1 TB Exceed Pass ($8.99)", desc: "No account or yearly commitment required. Stored for 14 days with unlimited downloads." },
      { title: "Resumable across days", desc: "IndexedDB state lets you pause and resume multi-hundred-gigabyte uploads safely." },
    ],
    steps: [
      { name: "Activate 1 TB Exceed Pass", text: "Get an instant 16-character Pass Code with no signup." },
      { name: "Select your massive archive", text: "Use Chrome or Edge on desktop for optimal multi-hundred-GB streaming." },
      { name: "Recipient streams to disk", text: "File System Access API writes decrypted bytes straight to the hard drive." },
    ],
    faq: [
      {
        q: "Can a browser really upload or download a 500 GB file?",
        a: "Yes, on desktop Chromium browsers (Chrome, Edge, Brave). Because LimeShare uses Web Streams and the File System Access API, data flows directly between disk and network without buffering in RAM.",
      },
    ],
  },
  {
    slug: "send-folders-without-zipping",
    category: "device",
    badge: "Folder Sharing",
    title: "Send Folders Online Without Zipping (Up to 500 Files Free)",
    description: "Upload entire folders and subfolders directly in your browser without creating a ZIP first. Preserves structure, encrypted end-to-end, 5 GB free.",
    h1: "Send Entire Folders Online Without Zipping First",
    lead: "Manually compressing a 4 GB project directory into a ZIP file takes time and doubles disk space usage on your laptop. With LimeShare, click 'Select Folder' to upload up to 500 files directly — and let the recipient download individual files or a single streaming ZIP.",
    keyPoints: [
      { title: "No local disk space wasted on temporary ZIPs", desc: "Select your project folder directly; relative paths are preserved inside the encrypted manifest." },
      { title: "Up to 500 files free (2,500 with Exceed)", desc: "Ideal for photo shoots, code repositories, CAD sheets, and audio stems." },
      { title: "Recipient chooses how to download", desc: "Recipients can preview single files or click 'Download all (ZIP)' to stream a ZIP64 archive on the fly." },
    ],
    steps: [
      { name: "Click 'Select Folder'", text: "Pick any folder from your computer on the LimeShare homepage." },
      { name: "Encrypt & upload", text: "Every file in the folder is encrypted with its own HKDF-derived AES-256 key." },
      { name: "Share one link", text: "Recipient browses all files or downloads the whole bundle as one ZIP." },
    ],
    faq: [
      {
        q: "Do I have to ZIP my folder before uploading?",
        a: "No. Click 'Select Folder' on the homepage and choose the folder directly.",
      },
    ],
  },
  {
    slug: "instagram-original-video",
    category: "device",
    badge: "Social Media Quality",
    title: "Send Original Uncompressed Video on Instagram DMs (4K 60fps)",
    description: "Instagram posts, Reels, and DMs aggressively compress video. Share a LimeShare link in Instagram DMs to deliver 100% original 4K video.",
    h1: "Send Uncompressed Original Video on Instagram",
    lead: "Sending a video through Instagram DMs or uploading to Reels re-encodes the file, crushes shadow detail, and strips 10-bit HDR color. When delivering video to a client, sponsor, or collaborator on Instagram, paste a LimeShare link instead.",
    keyPoints: [
      { title: "Zero re-encoding or bitrate loss", desc: "Deliver the exact MP4 or MOV master file from your camera roll." },
      { title: "In-browser video player", desc: "Recipient can watch the preview or save the uncompressed master to their phone." },
      { title: "5 GB free without registration", desc: "Enough for 10+ minutes of 4K 60fps footage." },
    ],
    steps: [
      { name: "Upload video on LimeShare", text: "Pick the video from your phone or computer." },
      { name: "Copy the link", text: "Generated in seconds with no account." },
      { name: "Paste into Instagram DM", text: "Recipient taps the link and saves the original file." },
    ],
    faq: [
      {
        q: "Does Instagram alter files sent via a LimeShare link?",
        a: "No. Instagram only sees the text URL; the video bytes stream directly from encrypted storage to your recipient's device.",
      },
    ],
  },
  {
    slug: "share-original-tiktok-videos",
    category: "device",
    badge: "Creator Workflow",
    title: "Share Original TikTok Source Videos Without Compression or Watermark",
    description: "Deliver raw, unwatermarked 4K TikTok source videos to editors, brands, and cross-posting teams. Up to 5 GB free, zero compression.",
    h1: "Share Uncompressed TikTok Source Videos with Brands & Editors",
    lead: "Downloading a published TikTok video gives you a heavily compressed 1080p file with a watermark. Creators, UGC agencies, and video editors use LimeShare to hand off raw, unwatermarked 4K source MOV/MP4 files in seconds.",
    keyPoints: [
      { title: "Clean, unwatermarked master files", desc: "Perfect for cross-posting to YouTube Shorts, Instagram Reels, and brand ad accounts." },
      { title: "Keep brand deliverables online for 1 year", desc: "Use KeepAlive Extend ($1.99) so sponsors can access UGC deliverables anytime." },
      { title: "Fast mobile upload", desc: "Upload straight from iPhone Safari or Android Chrome." },
    ],
    steps: [
      { name: "Select raw video export", text: "Choose your CapCut, Premiere, or Final Cut export." },
      { name: "Get encrypted link", text: "Zero compression, 100% original frame rate." },
      { name: "Send to brand or editor", text: "Recipient previews and downloads in 1 tap." },
    ],
    faq: [
      {
        q: "Can I send multiple UGC video variations in one link?",
        a: "Yes. Add up to 500 files (5 GB total) in a single free transfer.",
      },
    ],
  },
  {
    slug: "share-database-backups",
    category: "persona",
    badge: "DevOps & Data",
    title: "Share SQL Dumps & Database Backups Securely (Zero-Knowledge)",
    description: "Transfer PostgreSQL, MySQL, SQLite, and MongoDB database dumps with client-side AES-256-GCM encryption and Certified Destruction receipts.",
    h1: "Secure Zero-Knowledge Transfer for Database Backups & SQL Dumps",
    lead: "A database backup (.sql, .dump, .bak) contains your entire company's customer records, password hashes, and financial transactions. Uploading a SQL dump to Google Drive, Slack, or standard file-sharing sites exposes your database to catastrophic breaches. LimeShare seals dumps in your browser before a single byte touches the network.",
    keyPoints: [
      { title: "Zero-Knowledge AES-256-GCM + Argon2id", desc: "Wrap the link with an Argon2id password so even an intercepted link is useless." },
      { title: "Burn after first download", desc: "The encrypted dump is permanently erased from storage the moment your developer pulls it." },
      { title: "Certified Destruction receipt", desc: "Cryptographically signed proof of erasure for SOC2, ISO 27001, and GDPR compliance logs." },
    ],
    steps: [
      { name: "Select .sql / .dump / .bak file", text: "Up to 5 GB free, or up to 1 TB with Exceed." },
      { name: "Enable Burn-on-Read & Password", text: "Ensure single-use delivery with secondary password protection." },
      { name: "Archive Destruction Certificate", text: "Save the signed deletion receipt once the transfer is purged." },
    ],
    faq: [
      {
        q: "Can LimeShare or cloud providers read our SQL dump?",
        a: "Mathematically impossible. The dump is encrypted into 4 MiB AES-256-GCM frames inside your browser; the key never leaves the URL fragment.",
      },
    ],
  },
];
